import * as React from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";

const columns: GridColDef[] = [
  {
    field: "id",
    headerName: "Order Id",
    flex: 1,
    align: "center",
    headerAlign: "center",
    valueGetter: (params) => {
      const idStr = params.row.id?.toString() || "";
      return "#" + idStr.slice(-6);
    },
  },
  {
    field: "totalRemainingAmount",
    headerName: "Full Amounts",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "withdrawableAmount",
    headerName: "Withdrawable Amount",
    flex: 2,
    align: "center",
    headerAlign: "center",
    valueGetter: (params) => {
      const total = Number(params.row.totalRemainingAmount) || 0;
      const fees = Number(params.row.totalTransactionFees) || 0;
      const withdrawable = total - fees;
      return (Math.round(withdrawable * 100) / 100).toFixed(2);
    },
  },
  {
    field: "totalTransactionFees",
    headerName: "Total Transaction Fees",
    flex: 2,
    align: "center",
    headerAlign: "center",
    valueFormatter: (param) => (param.value != null ? Number(param.value).toFixed(2) : ""),
  },
  {
    field: "remainingTipsAmount",
    headerName: "Total Tips",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "totalVat",
    headerName: "Total Vat",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "totalSd",
    headerName: "Total Sd",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "remainingServiceChargeAmount",
    headerName: "Total Service Charge",
    sortable: false,
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "remainingServiceChargeVatAmount",
    headerName: "Total Service Charge VAT",
    sortable: false,
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "orderDate",
    headerName: "Order Date",
    flex: 2,
    minWidth: 160,
    align: "center",
    headerAlign: "center",
    valueGetter: (params) => params.row._formattedOrderDate || params.row.orderDate,
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      const orderDate = new Date(params.row.orderDate);
      const now = new Date();
      const diffHours = (now.getTime() - orderDate.getTime()) / (1000 * 60 * 60);
      const isActive = diffHours > 24;
      return (
        <span style={{ color: isActive ? "green" : "red", fontWeight: 600 }}>
          {isActive ? "Active" : "Pending"}
        </span>
      );
    },
  },
];

export default function WithdrawDataTable({
  tableData,
  selectOrder,
  showUnpaidOnly = false,
}: {
  tableData: any[];
  selectOrder: (orders: any[]) => void;
  showUnpaidOnly?: boolean;
}) {
  const [rowSelectionModel, setRowSelectionModel] = React.useState<string[]>([]);
  const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 5 });
  const [loading, setLoading] = React.useState(false);
  const rows = React.useMemo(() => tableData, [tableData]);

  const calculateWithdrawableAmount = React.useCallback((order: any) => {
    const total = Number(order.totalRemainingAmount) || 0;
    const fees = Number(order.totalTransactionFees) || 0;
    return Math.round((total - fees) * 100) / 100;
  }, []);

  const handleRowSelectionChange = React.useCallback(
    (newSelection: string[]) => {
      const validOrders = rows.filter(
        (row) =>
          newSelection.includes(row.id) &&
          (Date.now() - new Date(row.orderDate).getTime()) / (1000 * 60 * 60) > 24 &&
          calculateWithdrawableAmount(row) > 0
      );

      const invalidIds = newSelection.filter((id) => !validOrders.some((r) => r.id === id));
      if (invalidIds.length > 0) {
        const invalidOrders = invalidIds.map((id) => rows.find((r) => r.id === id)).filter(Boolean);
        const idsText = invalidOrders.map((o) => `#${o.id.toString().slice(-6)}`).join(", ");
        toast.error(`Cannot select: ${idsText}. Order <24h old or withdrawable amount ≤ 0.`);
      }

      const validIds = validOrders.map((r) => r.id);
      setRowSelectionModel(validIds);
      selectOrder(validOrders);
    },
    [rows, calculateWithdrawableAmount, selectOrder]
  );

  React.useEffect(() => {
    if (rowSelectionModel.length === 0) selectOrder([]);
  }, [rowSelectionModel, selectOrder]);

  return (
    <Paper sx={{ height: 400, width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading} // ✅ just use this
        rowCount={rows.length}
        pagination
        pageSizeOptions={[5, 10]}
        paginationModel={paginationModel}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleRowSelectionChange}
        isRowSelectable={(params) => {
          const diffHours = (Date.now() - new Date(params.row.orderDate).getTime()) / (1000 * 60 * 60);
          const withdrawable = calculateWithdrawableAmount(params.row);
          return diffHours > 24 && withdrawable > 0;
        }}
        onPaginationModelChange={(model) => {
          setLoading(true);
          setPaginationModel(model);

          // simulate fetch delay
          setTimeout(() => setLoading(false), 500);
        }}
        sx={{ border: 0 }}
      />
    </Paper>
  );
}
